import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { IFetchDiagnosesParams, IDiagnosis } from '~/interfaces'
import { fetchDiagnoses, message } from '~/services'
import { hasObjectValue } from '~/utils'

const UNIQUE_KEY = 'diagnoses'

type IUseFetchDiagnoses = UseQueryResult<IDiagnosis[], Error> & {
  getDiagnoses: (params: IFetchDiagnosesParams) => void
}

export const useFetchDiagnoses = (
  fetchParamsInitialState: IFetchDiagnosesParams | null = null,
  config?: UseQueryOptions<IDiagnosis[], Error>
): IUseFetchDiagnoses => {
  const [fetchParams, setFetchParams] = useState<IFetchDiagnosesParams | null>(fetchParamsInitialState)
  const hasFetchParams = hasObjectValue({ ...fetchParams })

  const fetcher = async () => {
    if (!fetchParams) throw new Error('fetchParams is required')

    try {
      const response = await fetchDiagnoses(fetchParams)
      const isResponseEmpty = response.length === 0

      if (isResponseEmpty) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw message('diagnoses.not_found')
      }

      return response
    } catch (error) {
      if (typeof error === 'string') throw new Error(error)
      throw new Error(message('messages.genericUnexpectedError'))
    }
  }

  const getDiagnoses = useCallback((params: IFetchDiagnosesParams) => {
    setFetchParams(params)
  }, [])

  return {
    ...useQuery<IDiagnosis[], Error>([UNIQUE_KEY, fetchParams?.patientId], fetcher, {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: hasFetchParams,
      ...config
    }),
    getDiagnoses
  }
}
